import { createElement, ReactNode } from 'react';
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid';
import { TFunction } from 'i18next';
import { AffectedHardware, AffectedSoftware, UserInfo, VisibilityColumnsOverview } from '@/types';
import { YES, fieldsFromDialog, fieldsFromMBOS, tablesWithAddDialogInfoRedux } from './constants';
import { equalArraysEqual } from './utils';

export const columnsSoftwareCodingParts = (
  software: AffectedSoftware,
  isRDMP = false,
  isUUID = false,
  mandatoryColumnsCoding: (label: string) => string | ReactNode,
  t: TFunction,
) => [
  { name: 'id', tiltle: '' },
  { name: isUUID ? 'uuidCode' : 'numberNew', title: mandatoryColumnsCoding(isUUID ? 'uuidCodeNew' : 'partNoNewNumber') },
  { name: 'description', title: t('description.label') },
  { name: isUUID ? 'uuidCodeOld' : 'numberOld', title: mandatoryColumnsCoding(isUUID ? 'uuidCodeOld' : 'partNoOld') },
  ...((software?.otaUpdate === YES && software.coding)
    ? [{ name: 'diogenesName', title: mandatoryColumnsCoding('diogenesName') },
      { name: 'ecuIsCodeable', title: t('software.ecuIsCodeable') },
      { name: isUUID ? 'hwUuidNo' : 'hwPartNo', title: mandatoryColumnsCoding(isUUID ? 'hwUuidNo' : 'hwPartNo') }]
    : []
  ),
  { name: 'modelSeries', title: t('software.modelSeries') },
  { name: 'typeVersions', title: t('software.typeVersions') },
  { name: 'codeCondition', title: t('software.codeCondition') },
  { name: 'affectedPlants', title: t('software.affectedPlants') },
  { name: 'additionalInformation', title: t('software.additionalInformation') },
  { name: 'zgsOld', title: t('software.zgsold') },
  { name: 'zgsNew', title: t('software.zgsnew') },
  ...(!isUUID ? [{ name: 'ywpVersionOld', title: t('software.ywpVersionOld') }, { name: 'ywpVersionNew', title: t('software.ywpVersionNew') }] : []),
  { name: 'kemOld', title: t('software.kemOld') },
  { name: 'kemNew', title: t('software.kemNew') },
  { name: 'qnt', title: t('software.qnt') },
  { name: 'meaning', title: t('software.meaning') },
  ...(isRDMP ? [{ name: isUUID ? 'uuidPersonResponsible' : 'btvPerson', title: t(`dialog.${isUUID ? 'uuidPersonResponsible' : 'btvPerson'}`) }] : []),
  ...(!isUUID ? [{ name: 'uuidCode', title: t('software.uuidCode') }] : []),
];

// Table Rework Coding ECUs
export const columnsExtensionSoftwareCodingParts = (software: AffectedSoftware, isUUID = false, isRDMP = false) => [
  { columnName: isUUID ? 'uuidCode' : 'numberNew', width: 200, wordWrapEnabled: true },
  { columnName: 'description', width: 200, wordWrapEnabled: true },
  ...((software?.otaUpdate === YES && software.coding)
    ? [{ columnName: 'diogenesName', width: 200 },
      { columnName: 'ecuIsCodeable', width: 220 },
      { columnName: isUUID ? 'hwUuidNo' : 'hwPartNo', width: 200 }]
    : []
  ),
  { columnName: isUUID ? 'uuidCodeOld' : 'numberOld', width: 200, wordWrapEnabled: true },
  { columnName: 'modelSeries', width: 200, wordWrapEnabled: true },
  { columnName: 'typeVersions', width: 200, wordWrapEnabled: true },
  { columnName: 'codeCondition', width: 200, wordWrapEnabled: true },
  { columnName: 'affectedPlants', width: 200, wordWrapEnabled: true },
  { columnName: 'additionalInformation', width: 300, wordWrapEnabled: true },
  { columnName: 'zgsOld', width: 200, wordWrapEnabled: true },
  { columnName: 'zgsNew', width: 200, wordWrapEnabled: true },
  { columnName: 'ywpVersionOld', width: 200, wordWrapEnabled: true },
  { columnName: 'ywpVersionNew', width: 200, wordWrapEnabled: true },
  { columnName: 'kemOld', width: 200, wordWrapEnabled: true },
  { columnName: 'kemNew', width: 200, wordWrapEnabled: true },
  { columnName: 'qnt', width: 200, wordWrapEnabled: true },
  { columnName: 'meaning', width: 200, wordWrapEnabled: true },
  ...(isRDMP ? [{ columnName: isUUID ? 'uuidPersonResponsible' : 'btvPerson', width: 270, wordWrapEnabled: true }] : []),
  ...(!isUUID ? [{ columnName: 'uuidCode', width: 200, wordWrapEnabled: true }] : []),
];

export const columnsSoftwareFlashingParts = (
  software: AffectedSoftware,
  isRDMP = false,
  isUUID = false,
  mandatoryColumnsFlashing: (label: string) => string | ReactNode,
  t: TFunction,
) => [
  { name: 'id', tiltle: '' },
  ...(isUUID ? [{ name: 'uuidCode', title: mandatoryColumnsFlashing('uuidCodeNew') }] : [{ name: 'numberNew', title: mandatoryColumnsFlashing('partNoNewNumber') }]),
  { name: 'description', title: t('description.label') },
  ...((software?.otaUpdate === YES && software.flashing)
    ? [{ name: 'diogenesName', title: isUUID ? t('software.diogenesName') : mandatoryColumnsFlashing('diogenesName') },
      { name: 'ecuIsCodeable', title: t('software.ecuIsCodeable') },
      ...(isUUID ? [{ name: 'hwUuidNo', title: mandatoryColumnsFlashing('hwUuidNo') }] : [{ name: 'hwPartNo', title: mandatoryColumnsFlashing('hwPartNo') }])]
    : []
  ),
  { name: isUUID ? 'uuidCodeOld' : 'numberOld', title: mandatoryColumnsFlashing(isUUID ? 'uuidCodeOld' : 'partNoOld') },
  { name: 'modelSeries', title: t('software.modelSeries') },
  { name: 'typeVersions', title: t('software.typeVersions') },
  { name: 'codeCondition', title: t('software.codeCondition') },
  { name: 'affectedPlants', title: t('software.affectedPlants') },
  { name: 'additionalInformation', title: t('software.additionalInformation') },
  { name: 'zgsOld', title: t('software.zgsold') },
  { name: 'zgsNew', title: t('software.zgsnew') },
  ...(!isUUID ? [{ name: 'ywpVersionOld', title: t('software.ywpVersionOld') }, { name: 'ywpVersionNew', title: mandatoryColumnsFlashing('ywpVersionNew') }] : []),
  { name: 'kemOld', title: t('software.kemOld') },
  { name: 'kemNew', title: t('software.kemNew') },
  ...(isRDMP ? [{ name: isUUID ? 'uuidPersonResponsible' : 'btvPerson', title: t(`dialog.${isUUID ? 'uuidPersonResponsible' : 'btvPerson'}`) }] : []),
  ...(!isUUID ? [{ name: 'uuidCode', title: t('software.uuidCode') }] : []),
];

export const columnsExtensionSoftwareFlashingParts = (software: AffectedSoftware, isUUID = false, isRDMP = false) => [
  { columnName: isUUID ? 'uuidCode' : 'numberNew', width: 200, wordWrapEnabled: true },
  { columnName: 'description', width: 200, wordWrapEnabled: true },
  ...((software?.otaUpdate === YES && software.flashing)
    ? [{ columnName: 'diogenesName', width: 200 },
      { columnName: 'ecuIsCodeable', width: 220 },
      { columnName: isUUID ? 'hwUuidNo' : 'hwPartNo', width: 200 }]
    : []
  ),
  { columnName: isUUID ? 'uuidCodeOld' : 'numberOld', width: 200, wordWrapEnabled: true },
  { columnName: 'modelSeries', width: 200, wordWrapEnabled: true },
  { columnName: 'typeVersions', width: 200, wordWrapEnabled: true },
  { columnName: 'codeCondition', width: 200, wordWrapEnabled: true },
  { columnName: 'affectedPlants', width: 200, wordWrapEnabled: true },
  { columnName: 'additionalInformation', width: 300, wordWrapEnabled: true },
  { columnName: 'zgsOld', width: 200, wordWrapEnabled: true },
  { columnName: 'zgsNew', width: 200, wordWrapEnabled: true },
  { columnName: 'kemOld', width: 200, wordWrapEnabled: true },
  { columnName: 'kemNew', width: 200, wordWrapEnabled: true },
  { columnName: 'ywpVersionOld', width: 200, wordWrapEnabled: true },
  { columnName: 'ywpVersionNew', width: 200, wordWrapEnabled: true },
  ...(isRDMP ? [{ columnName: isUUID ? 'uuidPersonResponsible' : 'btvPerson', width: 270, wordWrapEnabled: true }] : []),
  ...(!isUUID ? [{ columnName: 'uuidCode', width: 200, wordWrapEnabled: true }] : []),
];

export const columnsSoftwareBlockVehicleParts = (
  software: AffectedSoftware,
  isUUID = false,
  mandatoryColumnsBlock: (label: string) => string | ReactNode,
  t: TFunction,
) => [
  { name: 'id', tiltle: '' },
  { name: 'doormanBundleId', title: mandatoryColumnsBlock('doormanBundle') },
  { name: 'affectedMarkets', title: t('software.affectedMarket') },
  { name: isUUID ? 'uuidNaming' : 'partNaming', title: mandatoryColumnsBlock(isUUID ? 'uuidNaming' : 'partNaming') },
  { name: isUUID ? 'uuidCode' : 'partNumber', title: mandatoryColumnsBlock(isUUID ? 'uuidNumber' : 'partNumber') },
  { name: 'qntNumber', title: t('software.qntNumber') },
  { name: 'expectedRelease', title: t('software.expectedDoorman') },
  { name: 'sbmStatus', title: t('software.sbmStatus') },
  { name: 'freeText', title: t('software.doormanAditionalInfo') },
];

export const columnsExtensionSoftwareBlockVehicleParts = (software: AffectedSoftware, isUUID = false) => [
  { columnName: 'doormanBundleId', width: 200, wordWrapEnabled: true },
  { columnName: 'affectedMarkets', width: 250, wordWrapEnabled: true },
  { columnName: isUUID ? 'uuidNaming' : 'partNaming', width: 200, wordWrapEnabled: true },
  { columnName: isUUID ? 'uuidCode' : 'partNumber', width: 200, wordWrapEnabled: true },
  { columnName: 'qntNumber', width: 200, wordWrapEnabled: true },
  { columnName: 'expectedRelease', width: 200, wordWrapEnabled: true },
  { columnName: 'sbmStatus', width: 200, wordWrapEnabled: true },
  { columnName: 'freeText', width: 350, wordWrapEnabled: true },
];

export const compareSyncRowField = (fieldOne: string | UserInfo, fieldTwo: string | UserInfo, fieldName: string) => (fieldName === 'btvPerson'
  ? ((fieldOne as UserInfo) !== null && (fieldOne as UserInfo)?.code !== '' && (fieldOne as UserInfo)?.code === (fieldTwo as UserInfo)?.code)
  : (fieldOne !== '' && fieldOne === fieldTwo));

export const processEditFieldSynchronization = (row) => ({
  ...row,
  dialogSynchronization: {
    ...row.dialogSynchronization,
    dialogFieldsSynchronized: fieldsFromDialog.filter((field) => (
      compareSyncRowField(row[field], row?.dialogSynchronization?.dialogUpdateInitialData?.[field], field))),
  },
  ...(row?.mbosSynchronization && { mbosSynchronization: {
    ...row.mbosSynchronization,
    synchronizedFields: fieldsFromMBOS.filter((field) => (
      compareSyncRowField(row[field], row.mbosSynchronization?.synchronizedData?.[field], field))),
  } }),
});

export const initDialogSynchronization = (row) => ({
  ...row.dialogSynchronization,
  dialogUpdateInitialData: (row?.dialogSynchronization?.dialogFieldsSynchronized).reduce((acc, field) => {
    acc[field] = row[field];

    return acc;
  }, {}),
});

export const initDialogUpdateInitialData = (stateData: AffectedSoftware | AffectedHardware) => {
  let newState = stateData;
  tablesWithAddDialogInfoRedux.forEach((table) => {
    if (table in stateData) {
      newState = {
        ...newState,
        [table]: newState[table].map((row) => ((row?.dialogSynchronization?.updatedByDialog)
          ? ({
            ...row,
            dialogSynchronization: initDialogSynchronization(row),
          })
          : row)),
      };
    }
  });

  return newState;
};

export const hasDialogFieldsSynchronized = (table) => table.some((row) => row?.dialogSynchronization?.dialogFieldsSynchronized?.length > 0);

export const hasMBOSFieldsSynchronized = (table) => table.some((row) => row?.mbosSynchronization?.synchronizedFields?.length > 0);

export const mergeColumnExtensions = <T extends TableColumnWidthInfo | VisibilityColumnsOverview>(backendColumns: T[], defaultColumn: T[]): T[] => {
  const newColumns = defaultColumn.filter((defaultCol) => backendColumns.every((item) => item.columnName !== defaultCol.columnName));
  const backendDataColumns = backendColumns.filter((backendCol) => defaultColumn.some((item) => item.columnName === backendCol.columnName));

  backendDataColumns.forEach((backendCol) => {
    const defaultCol = defaultColumn.find((item) => item.columnName === backendCol.columnName);

    if (defaultCol && 'width' in backendCol && 'width' in defaultCol) {
      backendCol.width = Math.max(backendCol.width as number, defaultCol.width as number);
    }
  });

  return [...backendDataColumns, ...newColumns];
};

export const columnsOrderMerge = (backendColumns: string[], defaultColumn: string[], targetProperty?: string): string[] => {
  let columnOrder: string[] = [...defaultColumn];
  const newColumns = defaultColumn.filter((defaultColName) => backendColumns.every((backendColumnsName) => backendColumnsName !== defaultColName));
  const defaultCommonColumns = defaultColumn.filter((defaultColName) => backendColumns.some((backendColumnsName) => backendColumnsName === defaultColName));
  const backendCommonColumns = backendColumns.filter((backendColumnsName) => defaultColumn.some((defaultColName) => defaultColName === backendColumnsName));

  if (!equalArraysEqual(backendCommonColumns, defaultCommonColumns)) {
    const propertyIndex = backendCommonColumns.findIndex((column) => column === targetProperty);

    if (propertyIndex !== -1) {
      backendCommonColumns.splice(propertyIndex + 1, 0, ...newColumns);
      columnOrder = backendCommonColumns;
    } else {
      columnOrder = [...backendCommonColumns, ...newColumns];
    }
  }

  return columnOrder;
};

export const processSorting = (sort: string) => {
  const newSort = sort?.endsWith('.value') ? sort?.slice(0, -6) : sort;

  if (newSort !== 'campaigns.type' && newSort !== '') {
    return `${newSort}.value`;
  }

  return newSort;
};

export const langFormFormatter = (
  value: string,
  form: Record<string, Record<string, string>>,
  lang: string,
): string => form[value]?.[lang] || value;

export const WysiwygFormatter = ({ value }: { value: string }) => (
  createElement('div', {
    className: 'plain-html',
    dangerouslySetInnerHTML: { __html: value },
  })
);
